import { useEffect, useRef, useState } from "react";
import { Controller, useController } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import i18next from "i18next";
import { FIELD_REQUIRED } from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startSyncAdsAccountsEntities } from "../../actions/user";
import { ADS_INTEGRATION_ENTITIES, META } from "../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const buildPagesOptionsKey = (pages = {}) =>
  Object.keys(pages)
    .sort()
    .map((key) => String(pages[key]?.id ?? key))
    .join("|");

const SelectPageFacebook = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  integrationID,
  adAccountID,
  sxTextField = {},
  onChangeAction = () => {},
  excludePageIds = [],
  disableAutoSelect = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [isUpdatingPages, setIsUpdatingPages] = useState(false);
  const error = getReactHookFormNestedError(errors, name);
  const {
    field: { value: fieldValue, onChange: fieldOnChange },
  } = useController({
    name,
    control,
  });
  const dispatch = useDispatchApp();
  const { isLoading, integrations } = useSelectorApp((state) => state.user);
  const metaIntegrations = integrations?.facebook;
  const integration = metaIntegrations?.[integrationID] || {};
  const pagesOptionsKey = buildPagesOptionsKey(integration?.pages);
  const optionsKeyRef = useRef("");

  useEffect(() => {
    if (!integrationID) {
      return;
    }
    setIsUpdatingPages(true);
    dispatch(
      startSyncAdsAccountsEntities({
        integrationID,
        platform: META,
        entity: ADS_INTEGRATION_ENTITIES.PAGES,
      })
    ).finally(() => {
      setIsUpdatingPages(false);
    });
  }, [integrationID, dispatch]);

  useEffect(() => {
    const { pages = {} } = integration || {};
    const pagesKeys = Object.keys(pages);
    const pagesAccount = pagesKeys.map((key) => ({
      ...pages[key],
      integrationID: integration.id,
    }));
    const excludeSet = new Set((excludePageIds || []).map(String));
    const pagesFiltered = pagesAccount.filter(
      (p) => !excludeSet.has(String(p.id))
    );
    const nextOptionsKey = pagesFiltered.map((p) => String(p.id)).join("|");
    if (nextOptionsKey !== optionsKeyRef.current) {
      optionsKeyRef.current = nextOptionsKey;
      setOptions(pagesFiltered);
    }

    if (!disableAutoSelect && !fieldValue && pagesFiltered?.length > 0) {
      fieldOnChange(pagesFiltered[0].id);
      onChangeAction(pagesFiltered[0]);
    }

    // eslint-disable-next-line
  }, [
    pagesOptionsKey,
    integrationID,
    integration?.id,
    adAccountID,
    excludePageIds,
    disableAutoSelect,
    fieldValue,
  ]);

  if (isLoading) return null;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={""}
      rules={rules}
      render={({ field }) => (
        <Autocomplete
          {...field}
          hasLabel={Boolean(label)}
          value={options?.length > 0 ? field.value : ""}
          fullWidth={fullWidth}
          disabled={disabled}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          onChange={(_, value) => {
            field.onChange(value?.id);
            onChangeAction(value);
          }}
          isOptionEqualToValue={(option, value) => {
            if (value?.id) {
              return option?.id === value?.id;
            }
            return option?.id === value;
          }}
          getOptionLabel={(option) => {
            const optionValue = options.find((opt) => opt.id === option);
            if (optionValue) {
              return `${optionValue?.name}`;
            }
            if (typeof option === "string") {
              return option;
            }
            return `${option?.name}`;
          }}
          options={options}
          disableClearable={true}
          renderOption={(props, option) => {
            return (
              <Box
                {...props}
                component="li"
                key={option.id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Avatar
                  src={option.imageURL}
                  alt={option.name}
                  sx={{
                    width: 30,
                    height: 30,
                  }}
                />
                <Typography variant="body1">{option.name}</Typography>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              hiddenLabel={!label}
              label={label}
              variant={variant}
              placeholder={field?.value?.length > 0 ? "" : placeholder}
              error={!!error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isUpdatingPages ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              helperText={
                <Box
                  component="span"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{!!error ? error?.message : helperText}</span>
                </Box>
              }
              sx={{
                backgroundColor: "fields.background",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                ...sxTextField,
                ".MuiFormHelperText-root": {
                  ml: "0 !important",
                },
              }}
            />
          )}
        />
      )}
    />
  );
};

export default SelectPageFacebook;
